import { useMemo, useState, type ComponentType, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, LinearProgress, MenuItem, Paper, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography, alpha,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip as RTooltip, CartesianGrid } from 'recharts';
import { teamActivityService } from '../../services/team-activity.service';
import { useAuthStore } from '../../stores/auth.store';
import { useWorkspaceStore } from '../../stores/workspace.store';
import { useUrlState, codecs } from '../../hooks/useUrlState';
import { DATE_PRESETS, isDatePresetKey, resolveDatePreset, type DatePresetKey } from '../../utils/date-presets';
import { dayRangeParams } from '../../utils/timezone';
import ActivityHeatmap from './ActivityHeatmap';
import DayStrips from './DayStrips';
import {
  AREAS, DEVICES, type ColorBy, type Filters, areaColor, deviceColor, daysOf, eventLabel, fmtClock, fmtDuration,
  heatDominant, heatGrid, inTeamTime, mixByDay, teamToday, passes, rhythm,
} from './team-activity.format';

// recharts 2.x class components mistype under this repo's @types/react.
const RBar = Bar as unknown as ComponentType<Record<string, unknown>>;

type Metric = 'time' | 'mcp' | 'writes';

function Stat({ label, value, hint }: { label: string; value: ReactNode; hint?: string }) {
  return (
    <Paper variant="outlined" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`} sx={{ px: 2, py: 1.25, minWidth: 130, flex: '1 1 130px' }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="h6" fontWeight={600} lineHeight={1.3}>{value}</Typography>
      {hint && <Typography variant="caption" color="text.secondary">{hint}</Typography>}
    </Paper>
  );
}

function Section({ title, right, children }: { title: string; right?: ReactNode; children: ReactNode }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ flex: 1 }}>{title}</Typography>
        {right}
      </Box>
      {children}
    </Paper>
  );
}

const toggle = (list: string[], k: string) => (list.includes(k) ? list.filter((x) => x !== k) : [...list, k]);

export default function UserActivityPage() {
  const { userId = '' } = useParams();
  const navigate = useNavigate();
  const isAdmin = useAuthStore((s) => s.user?.role) === 'admin';
  const [preset, setPreset] = useUrlState<string>('range', 'last_30_days', codecs.str);
  const range = (isDatePresetKey(preset) ? preset : 'last_30_days') as DatePresetKey;
  const ws = useWorkspaceStore((s) => s.selectedIds.join(','));
  const win = useMemo(() => (dayRangeParams(resolveDatePreset(range, teamToday()))), [range]);
  const [filters, setFilters] = useState<Filters>({ areas: [], devices: [] });
  const [colorBy, setColorBy] = useState<ColorBy>('area');
  const [metric, setMetric] = useState<Metric>('time');

  const { data, isLoading, error } = useQuery({
    queryKey: ['team-activity', userId, win, ws],
    queryFn: () => teamActivityService.detail(userId, win),
    enabled: isAdmin && !!userId,
  });

  const days = useMemo(() => daysOf(win.date_from, win.date_to), [win]);
  const minutes = useMemo(() => (data?.minutes ?? []).filter((m) => passes(filters, m)), [data, filters]);
  const events = useMemo(() => (data?.events ?? []).filter((e) => passes({ ...filters, devices: [] }, e)), [data, filters]);
  const r = useMemo(() => rhythm(minutes), [minutes]);
  // Sessions describe the person's day; a filter that drops other areas would split them apart.
  const all = useMemo(() => rhythm(data?.minutes ?? []), [data]);
  const mcp = useMemo(() => events.filter((e) => e.channel === 'mcp'), [events]);
  const writes = useMemo(() => events.filter((e) => e.kind === 'write' && e.channel === 'ui'), [events]);
  const heat = metric === 'time' ? heatGrid(minutes) : heatGrid(metric === 'mcp' ? mcp : writes);
  const hues = useMemo(() => (metric === 'time' ? heatDominant(minutes, colorBy).map((k) => (k ? (colorBy === 'area' ? areaColor(k) : deviceColor(k)) : null)) : undefined), [metric, minutes, colorBy]);
  const legend = colorBy === 'area' ? AREAS : DEVICES;
  const mix = useMemo(() => mixByDay(minutes, days, colorBy), [minutes, days, colorBy]);
  const log = useMemo(() => [
    ...mcp.map((e) => ({ t: e.t, text: eventLabel(e) })),
    ...(data?.changes ?? []).map((c) => ({
      t: inTeamTime(c.created_at),
      text: `${c.action[0].toUpperCase()}${c.action.slice(1)} ${(c.entity_type ?? '').replace(/_/g, ' ')}${c.entity_name ? ` “${c.entity_name}”` : ''}`,
    })),
  ].sort((a, b) => b.t.localeCompare(a.t)), [mcp, data]);
  const [logLimit, setLogLimit] = useState(30);

  if (!isAdmin) return <Alert severity="warning">You do not have access to this page.</Alert>;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(`/team-activity?range=${range}`)} size="small">Team</Button>
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Typography variant="h5" fontWeight={600}>{data?.user.name ?? '…'}</Typography>
          <Typography variant="body2" color="text.secondary">{data?.user.email} {data && `· ${data.user.role.replace('_', ' ')}`}</Typography>
        </Box>
        <TextField select size="small" label="Period (Israel time)" value={range} onChange={(e) => setPreset(e.target.value)} sx={{ minWidth: 170 }}>
          {DATE_PRESETS.map((p) => <MenuItem key={p.key} value={p.key}>{p.title}</MenuItem>)}
        </TextField>
      </Box>
      {isLoading && <LinearProgress sx={{ mb: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }}>Could not load this user's activity.</Alert>}
      {data?.events_truncated && (
        <Alert severity="info" sx={{ mb: 2 }}>Showing the latest 20,000 changes and MCP calls — pick a shorter period for all of them.</Alert>
      )}

      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 2 }} data-testid="activity-filters">
        {AREAS.map((a) => (
          <Chip key={a.key} size="small" label={a.label} onClick={() => setFilters((f) => ({ ...f, areas: toggle(f.areas, a.key) }))}
            variant={filters.areas.includes(a.key) ? 'filled' : 'outlined'}
            sx={{ borderColor: a.color, ...(filters.areas.includes(a.key) && { bgcolor: alpha(a.color, 0.18), color: 'text.primary', border: 2, borderColor: a.color }) }} />
        ))}
        <Box sx={{ width: 12 }} />
        {DEVICES.map((d) => (
          <Chip key={d.key} size="small" label={d.label} onClick={() => setFilters((f) => ({ ...f, devices: toggle(f.devices, d.key) }))}
            variant={filters.devices.includes(d.key) ? 'filled' : 'outlined'}
            sx={{ borderColor: d.color, ...(filters.devices.includes(d.key) && { bgcolor: alpha(d.color, 0.18), color: 'text.primary', border: 2, borderColor: d.color }) }} />
        ))}
        {(filters.areas.length > 0 || filters.devices.length > 0) && (
          <Chip size="small" label="Clear" onClick={() => setFilters({ areas: [], devices: [] })} />
        )}
      </Stack>

      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
        <Stat label="Active time" value={fmtDuration(r.total)} hint={`${fmtDuration(r.perActiveDay)} per active day`} />
        <Stat label="Days active" value={`${r.days} / ${days.length}`} hint={`${Math.round(r.weekendShare * 100)}% on weekends`} />
        <Stat label="Typical day" value={r.typicalStart == null ? '—' : `${fmtClock(r.typicalStart)}–${fmtClock(r.typicalEnd! + 1)}`} hint="median start and end" />
        <Stat label="Sessions" value={all.sessions} hint={`longest ${fmtDuration(all.longest)}`} />
        <Stat label="Changes" value={writes.length} hint="made in the app" />
        <Stat label="MCP calls" value={mcp.length}
          hint={`${mcp.filter((e) => e.kind === 'read').length} reads · ${mcp.filter((e) => e.kind === 'write').length} writes`} />
      </Box>

      <Section title="When they work" right={(
        <>
          <ToggleButtonGroup size="small" exclusive value={metric} onChange={(_, v) => v && setMetric(v)}>
            <ToggleButton value="time">Active time</ToggleButton>
            <ToggleButton value="mcp">MCP calls</ToggleButton>
            <ToggleButton value="writes">Changes</ToggleButton>
          </ToggleButtonGroup>
          {metric === 'time' && (
            <ToggleButtonGroup size="small" exclusive value={colorBy} onChange={(_, v) => v && setColorBy(v)}>
              <ToggleButton value="area">By area</ToggleButton>
              <ToggleButton value="device">By device</ToggleButton>
            </ToggleButtonGroup>
          )}
        </>
      )}>
        <ActivityHeatmap values={heat} hues={hues} unit={metric === 'time' ? 'min' : 'calls'}
          format={(v) => (metric === 'time' ? fmtDuration(v) : `${v} ${metric === 'mcp' ? 'MCP calls' : 'changes'}`)} />
        {metric === 'time' && (
          <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap" sx={{ mt: 1.5 }}>
            {legend.map((l) => (
              <Box key={l.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: l.color }} />
                <Typography variant="caption">{l.label}</Typography>
              </Box>
            ))}
          </Stack>
        )}
      </Section>

      <Section title="Day by day" right={(
        <Typography variant="caption" color="text.secondary">red tick: a change · dark dot: an MCP call</Typography>
      )}>
        <DayStrips days={days} minutes={minutes} events={events} colorBy={colorBy} />
      </Section>

      <Section title={colorBy === 'area' ? 'Where the time went' : 'Which device'}>
        <Box sx={{ height: 220 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={mix} margin={{ top: 4, right: 8, bottom: 0, left: -10 }}>
              <CartesianGrid vertical={false} strokeOpacity={0.3} />
              <XAxis dataKey="day" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v: number) => fmtDuration(v)} width={56} />
              <RTooltip formatter={(v: number, k: string) => [fmtDuration(v), legend.find((l) => l.key === k)?.label ?? k]} />
              {legend.map((l) => <RBar key={l.key} dataKey={l.key} stackId="a" fill={l.color} />)}
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Section>

      <Section title="Changes and MCP calls">
        {log.length === 0 && <Typography variant="body2" color="text.secondary">None in this period.</Typography>}
        {log.slice(0, logLimit).map((e, i) => (
          <Box key={i} sx={{ display: 'flex', gap: 2, py: 0.5, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="caption" color="text.secondary" sx={{ width: 130, flexShrink: 0 }}>{e.t.slice(0, 16)}</Typography>
            <Typography variant="body2">{e.text}</Typography>
          </Box>
        ))}
        {log.length > logLimit && <Button size="small" sx={{ mt: 1 }} onClick={() => setLogLimit((n) => n + 100)}>Show more</Button>}
      </Section>
    </Box>
  );
}
